import { Button } from "@jugl-web/ui-kit";
import { Tab } from "@jugl-web/ui-components/cross-platform";
import { TableGrid } from "@jugl-web/ui-components";
import { getUniqueId, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CXAutomationAccordion } from "../CXAutomationAccordion";
import { SectionDescription } from "../SectionDescription";
import { CXAutomationFormState, HttpMethod, RequestTabId } from "../../types";
import {
  DataInputItem,
  buildDataInputTableColumns,
} from "./components/DataInputRow";
import {
  RequestRow,
  buildRequestKeyValueTableColumns,
} from "./components/RequestKeyValueRow";
import { buildCxAutomationSupportedVariables } from "./utils";
import { CXAutomationUrlEditor } from "./components/CXAutomationUrlEditor";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

const METHOD_ITEMS: { name: string; value: HttpMethod }[] = [
  { name: "Get", value: "GET" },
  { name: "Post", value: "POST" },
];

type ApiSetupSlice = Pick<
  CXAutomationFormState,
  "method" | "urlMarkdown" | "dataInputs" | "requestRowsByTab"
>;

type Props = {
  value: ApiSetupSlice;
  onChange: (next: Partial<ApiSetupSlice>) => void;
  isOpen: boolean;
  onOpenChange: (next: boolean) => void;
  onProceed: () => void;
};

export const CXAutomationApiSetupSection: React.FC<Props> = ({
  value,
  onChange,
  isOpen,
  onOpenChange,
  onProceed,
}) => {
  const { t } = useTranslations();
  const { method, urlMarkdown, dataInputs, requestRowsByTab } = value;
  // GET requests can't carry a body, so it would be silently dropped on save —
  // hide the Body tab for GET and fall back to Parameters if it was selected.
  const isGet = method === "GET";
  const [selectedRequestTab, setSelectedRequestTab] =
    useState<RequestTabId>("parameters");

  useEffect(() => {
    if (isGet && selectedRequestTab === "body") {
      setSelectedRequestTab("parameters");
    }
  }, [isGet, selectedRequestTab]);

  const createDataInputItem = (): DataInputItem => ({
    id: getUniqueId(),
    name: "",
    description: "",
  });

  const createRequestRow = (): RequestRow => ({
    id: getUniqueId(),
    key: "",
    value: "",
  });

  const activeRequestRows = requestRowsByTab[selectedRequestTab];

  const supportedVariables = useMemo(
    () => buildCxAutomationSupportedVariables(dataInputs),
    [dataInputs]
  );

  const handleDataInputChange = useCallback(
    (next: DataInputItem) => {
      onChange({
        dataInputs: dataInputs.map((current) =>
          current.id === next.id ? next : current
        ),
      });
    },
    [dataInputs, onChange]
  );

  const handleDataInputDelete = useCallback(
    (id: string) => {
      onChange({
        dataInputs: dataInputs.filter((current) => current.id !== id),
      });
    },
    [dataInputs, onChange]
  );

  const dataInputColumns = useMemo(
    () =>
      buildDataInputTableColumns(
        t,
        handleDataInputChange,
        handleDataInputDelete
      ),
    [t, handleDataInputChange, handleDataInputDelete]
  );

  const handleRequestRowChange = useCallback(
    (next: RequestRow) => {
      onChange({
        requestRowsByTab: {
          ...requestRowsByTab,
          [selectedRequestTab]: requestRowsByTab[selectedRequestTab].map(
            (current) => (current.id === next.id ? next : current)
          ),
        },
      });
    },
    [onChange, requestRowsByTab, selectedRequestTab]
  );

  const handleRequestRowDelete = useCallback(
    (id: string) => {
      onChange({
        requestRowsByTab: {
          ...requestRowsByTab,
          [selectedRequestTab]: requestRowsByTab[selectedRequestTab].filter(
            (current) => current.id !== id
          ),
        },
      });
    },
    [onChange, requestRowsByTab, selectedRequestTab]
  );

  const requestKeyValueColumns = useMemo(
    () =>
      buildRequestKeyValueTableColumns(
        t,
        handleRequestRowChange,
        handleRequestRowDelete,
        supportedVariables
      ),
    [t, handleRequestRowChange, handleRequestRowDelete, supportedVariables]
  );

  return (
    <CXAutomationAccordion
      number={2}
      title={t({
        id: "cx-actions-page.api-setup",
        defaultMessage: "API setup",
      })}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-[72px]">
          <div className="flex flex-col gap-6">
            <SectionDescription
              title={t({
                id: "cx-actions-page.collect-data-inputs",
                defaultMessage: "Collect Data inputs for Action",
              })}
              description={t({
                id: "cx-actions-page.collect-data-inputs-description",
                defaultMessage:
                  "Specify the data the AI Agent needs for this action. It can use chat history, ask the user directly, or retrieve it from available metadata",
              })}
            />

            {dataInputs.length > 0 && (
              <TableGrid
                id="cx-automation-api-data-inputs"
                variant="grid"
                inline
                data={dataInputs}
                columns={dataInputColumns}
                classNames={{
                  wrapper:
                    "rounded-xl border border-solid border-grey-300 overflow-visible",
                }}
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              className="w-[165px]"
              onClick={() =>
                onChange({
                  dataInputs: [...dataInputs, createDataInputItem()],
                })
              }
              iconEnd={<AddIcon />}
            >
              {t({
                id: "cx-actions-page.add-data-input",
                defaultMessage: "Add Data Input",
              })}
            </Button>
          </div>

          <div className="flex flex-col gap-8">
            <SectionDescription
              title={t({
                id: "cx-actions-page.api-request",
                defaultMessage: "API request",
              })}
              description={t({
                id: "cx-actions-page.api-request-description",
                defaultMessage:
                  "The API endpoint the AI Agent calls to fetch or send data, including any user-provided inputs in the request",
              })}
            />
            <CXAutomationUrlEditor
              method={method}
              onMethodChange={(next) =>
                onChange({ method: next as HttpMethod })
              }
              methodItems={METHOD_ITEMS}
              httpsRequest={urlMarkdown}
              onHttpsRequestChange={(next) => onChange({ urlMarkdown: next })}
              supportedVariables={supportedVariables}
              dataInputs={dataInputs}
            />
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex h-[46px] items-end pt-2">
              <Tab
                title={t({
                  id: "cx-actions-page.parameters",
                  defaultMessage: "Parameters",
                })}
                shouldScrollIntoViewOnSelect={false}
                count={requestRowsByTab.parameters.length}
                isSelected={selectedRequestTab === "parameters"}
                onClick={() => setSelectedRequestTab("parameters")}
                className="w-auto"
              />
              <Tab
                title={t({
                  id: "cx-actions-page.headers",
                  defaultMessage: "Headers",
                })}
                shouldScrollIntoViewOnSelect={false}
                count={requestRowsByTab.headers.length}
                isSelected={selectedRequestTab === "headers"}
                onClick={() => setSelectedRequestTab("headers")}
                className="w-auto"
              />
              {!isGet && (
                <Tab
                  title={t({
                    id: "cx-actions-page.body",
                    defaultMessage: "Body",
                  })}
                  shouldScrollIntoViewOnSelect={false}
                  count={requestRowsByTab.body.length}
                  isSelected={selectedRequestTab === "body"}
                  onClick={() => setSelectedRequestTab("body")}
                  className="w-auto"
                />
              )}
            </div>
            {activeRequestRows.length > 0 && (
              <TableGrid
                id={`cx-automation-api-request-rows-${selectedRequestTab}`}
                variant="grid"
                inline
                data={activeRequestRows}
                columns={requestKeyValueColumns}
                classNames={{
                  wrapper:
                    "rounded-xl border border-solid border-grey-300 overflow-visible",
                }}
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              className="w-[191px]"
              iconEnd={<AddIcon />}
              onClick={() =>
                onChange({
                  requestRowsByTab: {
                    ...requestRowsByTab,
                    [selectedRequestTab]: [
                      ...requestRowsByTab[selectedRequestTab],
                      createRequestRow(),
                    ],
                  },
                })
              }
            >
              {t({
                id: "cx-actions-page.add-key-value-pair",
                defaultMessage: "Add Key Value Pair",
              })}
            </Button>
          </div>
        </div>
        <Button
          className="w-[200px]"
          iconEnd={<ArrowIcon />}
          onClick={onProceed}
        >
          {t({
            id: "cx-actions-page.save-and-proceed",
            defaultMessage: "Save & Proceed",
          })}
        </Button>
      </div>
    </CXAutomationAccordion>
  );
};
